/**
 * Messages exchanged between the Store Editor (parent window) and the live
 * website preview running inside its iframe. Keep this file free of React and
 * of any server-only import: both sides load it.
 *
 * Every message carries the id of the short-lived preview session, and both
 * sides check the sender's origin against an allow-list built from the current
 * host. Anything else is ignored.
 */
import type { PageDocument } from "@/lib/cms/blocks";

export const PREVIEW_SOURCE = "truwe-store-editor-preview";
export const STUDIO_SOURCE = "truwe-store-editor";

export type PreviewRegion = { path: string; label: string; doc: PageDocument };

/** Preview → editor. */
export type PreviewMessage =
  | { source: typeof PREVIEW_SOURCE; sid: string; type: "ready"; canPublish: boolean }
  | { source: typeof PREVIEW_SOURCE; sid: string; type: "regions"; regions: PreviewRegion[] }
  | { source: typeof PREVIEW_SOURCE; sid: string; type: "select"; path: string; id: string }
  | { source: typeof PREVIEW_SOURCE; sid: string; type: "error"; message: string };

/** Editor → preview. */
export type StudioMessage =
  | { source: typeof STUDIO_SOURCE; sid: string; type: "docs"; docs: Record<string, PageDocument> }
  | { source: typeof STUDIO_SOURCE; sid: string; type: "select"; path: string; id: string | null }
  | { source: typeof STUDIO_SOURCE; sid: string; type: "ping" };

function shaped(v: unknown, source: string): boolean {
  if (!v || typeof v !== "object") return false;
  const m = v as { source?: unknown; sid?: unknown; type?: unknown };
  return m.source === source && typeof m.sid === "string" && typeof m.type === "string";
}

export function isPreviewMessage(v: unknown): v is PreviewMessage {
  return shaped(v, PREVIEW_SOURCE);
}

export function isStudioMessage(v: unknown): v is StudioMessage {
  return shaped(v, STUDIO_SOURCE);
}

/** `admin.truwe.in` → `truwe.in`. Anything else is returned unchanged. */
export function baseHost(hostname: string): string {
  return hostname.startsWith("admin.") ? hostname.slice(6) : hostname;
}

/**
 * Origins we are willing to talk to. The Admin lives on `admin.<host>` and the
 * storefront on `<host>` / `www.<host>`; in preview and local dev both sides
 * share one origin.
 */
export function allowedOrigins(current: { protocol: string; hostname: string; origin: string }): string[] {
  const base = baseHost(current.hostname);
  const p = current.protocol;
  return Array.from(
    new Set([current.origin, `${p}//${base}`, `${p}//www.${base}`, `${p}//admin.${base}`]),
  );
}

export function isAllowedOrigin(origin: string, current?: { protocol: string; hostname: string; origin: string }): boolean {
  const loc =
    current ??
    (typeof window === "undefined"
      ? null
      : {
          protocol: window.location.protocol,
          hostname: window.location.hostname,
          origin: window.location.origin,
        });
  if (!loc) return false;
  // Ports differ between dev servers on the same host — compare host+protocol.
  try {
    const got = new URL(origin);
    return allowedOrigins(loc).some((o) => {
      const want = new URL(o);
      return want.protocol === got.protocol && baseHost(want.hostname) === baseHost(got.hostname);
    });
  } catch {
    return false;
  }
}

/**
 * Where the preview should send its messages: the admin window that opened it.
 */
export function parentTargetOrigin(): string {
  if (typeof window === "undefined") return "*";
  try {
    const ref = document.referrer ? new URL(document.referrer).origin : "";
    if (ref && isAllowedOrigin(ref)) return ref;
    return window.location.origin;
  } catch {
    return window.location.origin;
  }
}
